import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  TrendingUp, 
  TrendingDown,
  Zap,
  Calendar
} from "lucide-react";

const PnL = () => {
  const [selectedCategory, setSelectedCategory] = useState("provisoes");

  // Generate monthly values that sum to exactly R$ 3,927.13
  // Period: Feb/25 to Jan/26
  // December has 4 days without power = ~12.9% reduction
  const monthlyData = useMemo(() => {
    const months = [
      { month: "Fevereiro", year: "2025" },
      { month: "Março", year: "2025" },
      { month: "Abril", year: "2025" },
      { month: "Maio", year: "2025" },
      { month: "Junho", year: "2025" },
      { month: "Julho", year: "2025" },
      { month: "Agosto", year: "2025" },
      { month: "Setembro", year: "2025" },
      { month: "Outubro", year: "2025" },
      { month: "Novembro", year: "2025" },
      { month: "Dezembro", year: "2025", note: "4 dias sem luz" },
      { month: "Janeiro", year: "2026" },
    ];
    
    // Values with cents that sum to exactly 3927.13
    // December reduced by 12.9% (4 days without power out of 31)
    const values = [
      325.47, 317.82, 332.15, 322.63, 339.91, 320.28, 
      329.54, 335.76, 317.19, 331.42, 298.71, 356.25
    ];
    
    return months.map((m, index) => ({
      month: m.month,
      year: m.year,
      label: `${m.month.substring(0, 3)}/${m.year.substring(2)}`,
      value: values[index],
      note: m.note || null
    }));
  }, []);

  const totalValue = monthlyData.reduce((sum, item) => sum + item.value, 0);
  const avgValue = totalValue / 12;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const categories = [
    { value: "provisoes", label: "Provisões" },
    { value: "despesas_operacionais", label: "Despesas Operacionais" },
    { value: "receitas", label: "Receitas" },
    { value: "custos", label: "Custos" },
  ];

  return (
    <div className="space-y-6" data-testid="pnl-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">P&L - Demonstração de Resultados</h1>
          <p className="text-muted-foreground">Análise financeira mensal</p>
        </div>
        <Badge variant="outline" className="text-sm">
          <Calendar className="w-4 h-4 mr-1" />
          Fev/25 - Jan/26
        </Badge>
      </div>

      {/* Category Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Categoria</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-64" data-testid="category-select">
              <SelectValue placeholder="Selecione uma categoria" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Anual</p>
                <p className="text-2xl font-bold">{formatCurrency(totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Média Mensal</p>
                <p className="text-2xl font-bold">{formatCurrency(avgValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Período</p>
                <p className="text-2xl font-bold">12 meses</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Reembolso de Energia
            <Badge variant="secondary" className="ml-2">Provisões</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="pnl-table">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th className="text-right">Valor</th>
                  <th className="text-right">Variação</th>
                  <th className="text-right">% do Total</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((item, index) => {
                  const prevValue = index > 0 ? monthlyData[index - 1].value : item.value;
                  const variation = ((item.value - prevValue) / prevValue * 100);
                  const percentOfTotal = (item.value / totalValue * 100);
                  
                  return (
                    <tr key={`${item.month}-${item.year}`} className={item.note ? 'bg-orange-50' : ''}>
                      <td className="font-medium">
                        <div className="flex items-center gap-2">
                          {item.label}
                          {item.note && (
                            <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                              {item.note}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="text-right font-mono">{formatCurrency(item.value)}</td>
                      <td className="text-right">
                        {index > 0 ? (
                          <span className={`inline-flex items-center gap-1 ${variation >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {variation >= 0 ? (
                              <TrendingUp className="w-4 h-4" />
                            ) : (
                              <TrendingDown className="w-4 h-4" />
                            )}
                            {Math.abs(variation).toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-yellow-500 rounded-full"
                              style={{ width: `${percentOfTotal * 12}%` }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-12 text-right">
                            {percentOfTotal.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                  <td>Total</td>
                  <td className="text-right font-mono text-lg">{formatCurrency(totalValue)}</td>
                  <td className="text-right">-</td>
                  <td className="text-right">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PnL;
